import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";

import { BsBoxSeam, BsDashLg, BsEye, BsPlusLg } from "react-icons/bs";

import { serviceUpdateProductQuantity } from "../../api/serviceApi";

import { useProducts } from "../../hooks/useProducts";
import { getImageUrl } from "../../utils/helper";

const ITEMS_PER_PAGE = 20;

// ============================================================
// HELPERS
// ============================================================

const getId = (item) => item?.id || item?._id || "";

const getQuantity = (product) => {
  const value = Number(product?.quantity);

  return Number.isFinite(value) ? value : 0;
};

const getMinimumQuantity = (product) => {
  /*
   * New API field:
   * minQuantity
   *
   * Keep the old fallback of 5 if no useful
   * minimum has been configured.
   */
  const value = Number(product?.minQuantity ?? product?.min_quantity);

  if (Number.isFinite(value) && value > 0) {
    return value;
  }

  return 5;
};

const isLowStock = (product) => {
  return getQuantity(product) < getMinimumQuantity(product);
};

// ============================================================
// COMPONENT
// ============================================================

const InventoryAlertTable = ({ searchQuery }) => {
  const { products, loadingProducts } = useProducts(true);

  /*
   * Keep a local copy so stock changes can be
   * reflected immediately after the API update.
   */
  const [inventoryProducts, setInventoryProducts] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);

  const [updatingId, setUpdatingId] = useState("");

  // ==========================================================
  // SYNC PRODUCTS
  // ==========================================================

  useEffect(() => {
    setInventoryProducts(Array.isArray(products) ? products : []);
  }, [products]);

  // ==========================================================
  // LOW STOCK
  // ==========================================================

  const lowStockProducts = useMemo(() => {
    return inventoryProducts.filter(isLowStock);
  }, [inventoryProducts]);

  // ==========================================================
  // SEARCH
  // ==========================================================

  const filteredProducts = useMemo(() => {
    const query = String(searchQuery || "")
      .trim()
      .toLowerCase();

    if (!query) {
      return lowStockProducts;
    }

    return lowStockProducts.filter((product) => {
      const values = [
        product.mpn,
        product.name,
        product.supplier,
        product.type,

        /*
         * New API field.
         */
        product.shelfAddress,

        /*
         * Temporary old-data fallback.
         */
        product.shelf_address,
      ];

      return values.some((value) =>
        String(value || "")
          .toLowerCase()
          .includes(query),
      );
    });
  }, [lowStockProducts, searchQuery]);

  // ==========================================================
  // PAGINATION
  // ==========================================================

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / ITEMS_PER_PAGE),
  );

  const currentProducts = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredProducts.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredProducts, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  /*
   * If updating a quantity makes a product
   * disappear from low-stock results, make sure
   * the current page still exists.
   */
  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  // ==========================================================
  // STOCK UPDATE
  // ==========================================================

  const updateStock = async (product, type) => {
    const productId = getId(product);

    if (!productId || updatingId) {
      return;
    }

    const actionText = type === "add" ? "add" : "subtract";

    const input = window.prompt(`How many items do you want to ${actionText}?`);

    /*
     * Cancel clicked.
     */
    if (input === null) {
      return;
    }

    const amount = Number(input);

    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Enter a valid quantity greater than zero.");

      return;
    }

    const currentQuantity = getQuantity(product);

    const nextQuantity =
      type === "add" ? currentQuantity + amount : currentQuantity - amount;

    if (nextQuantity < 0) {
      toast.error(
        `Only ${currentQuantity} item${
          currentQuantity === 1 ? "" : "s"
        } are currently in stock.`,
      );

      return;
    }

    try {
      setUpdatingId(productId);

      /*
       * serviceUpdateProduct accepts the update
       * object directly.
       *
       * Keeping the existing product fields makes
       * this safe if your update endpoint expects
       * the complete product rather than a partial
       * patch.
       */
      const payload = {
        id: productId,
        quantity: nextQuantity,
      };

      const result = await serviceUpdateProductQuantity(payload);

      /*
       * Update the row immediately.
       *
       * If the new quantity is now above minimum,
       * it automatically disappears from this alert
       * table because lowStockProducts is derived
       * from this state.
       */
      setInventoryProducts((current) =>
        current.map((item) => {
          if (getId(item) !== productId) {
            return item;
          }

          return {
            ...item,
            ...(result && typeof result === "object" ? result : {}),
            quantity: nextQuantity,
          };
        }),
      );

      toast.success(
        type === "add"
          ? `Added ${amount} to ${product.name || "product"} stock.`
          : `Removed ${amount} from ${product.name || "product"} stock.`,
      );
    } catch (error) {
      console.error("Error updating product stock:", error);

      toast.error(error?.message || "Failed to update product stock.");
    } finally {
      setUpdatingId("");
    }
  };

  // ==========================================================
  // LOADING
  // ==========================================================

  if (loadingProducts) {
    return (
      <div className="inventory-alert-state">
        <div className="inventory-alert-state-icon">
          <BsBoxSeam />
        </div>

        <strong>Loading inventory...</strong>

        <span>Checking products for low stock.</span>
      </div>
    );
  }

  // ==========================================================
  // EMPTY
  // ==========================================================

  if (lowStockProducts.length === 0) {
    return (
      <div className="inventory-alert-state success">
        <div className="inventory-alert-state-icon">
          <BsBoxSeam />
        </div>

        <strong>Stock levels look good</strong>

        <span>No products are currently below their minimum stock level.</span>
      </div>
    );
  }

  if (filteredProducts.length === 0) {
    return (
      <div className="inventory-alert-state">
        <div className="inventory-alert-state-icon">
          <BsBoxSeam />
        </div>

        <strong>No matching products</strong>

        <span>No low-stock products match your current search.</span>
      </div>
    );
  }

  // ==========================================================
  // TABLE
  // ==========================================================

  return (
    <>
      <div className="inventory-alert-table-card">
        <div className="inventory-alert-table-scroll">
          <table className="inventory-alert-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Minimum</th>
                <th>MPN</th>
                <th>Shelf</th>
                <th>Supplier</th>
                <th>Type</th>
                <th className="inventory-alert-action-heading">Actions</th>
              </tr>
            </thead>

            <tbody>
              {currentProducts.map((product) => {
                const id = getId(product);

                const quantity = getQuantity(product);

                const minimum = getMinimumQuantity(product);

                const imageUrl = getImageUrl(product.imageUrl);

                const updating = updatingId === id;

                return (
                  <tr key={id}>
                    {/* PRODUCT */}

                    <td>
                      <div className="inventory-alert-product">
                        <div className="inventory-alert-product-image">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={product.name || "Product"}
                              onError={(event) => {
                                event.currentTarget.style.display = "none";

                                event.currentTarget
                                  .closest(".inventory-alert-product-image")
                                  ?.classList.add("image-error");
                              }}
                            />
                          ) : (
                            <BsBoxSeam />
                          )}

                          <div className="inventory-alert-product-image-fallback">
                            <BsBoxSeam />
                          </div>
                        </div>

                        <div className="inventory-alert-product-details">
                          <strong>{product.name || "Unnamed Product"}</strong>

                          {product.mpn && <small>{product.mpn}</small>}
                        </div>
                      </div>
                    </td>

                    {/* QUANTITY */}

                    <td>
                      <span className="inventory-alert-quantity">
                        {quantity}
                      </span>
                    </td>

                    {/* MINIMUM */}

                    <td>
                      <span className="inventory-alert-minimum">{minimum}</span>
                    </td>

                    {/* MPN */}

                    <td>
                      <span className="inventory-alert-code">
                        {product.mpn || "—"}
                      </span>
                    </td>

                    {/* SHELF */}

                    <td>
                      {product.shelfAddress || product.shelf_address || "—"}
                    </td>

                    {/* SUPPLIER */}

                    <td>{product.supplier || "—"}</td>

                    {/* TYPE */}

                    <td>{product.type || "—"}</td>

                    {/* ACTIONS */}

                    <td>
                      <div className="inventory-alert-actions">
                        <Link
                          to={`/inventory/view/${id}`}
                          className="inventory-alert-action view"
                          title="View product"
                          aria-label={`View ${product.name || "product"}`}
                        >
                          <BsEye />
                        </Link>

                        <button
                          type="button"
                          className="inventory-alert-action add"
                          disabled={updating}
                          onClick={() => updateStock(product, "add")}
                          title="Add stock"
                          aria-label={`Add stock to ${product.name || "product"}`}
                        >
                          <BsPlusLg />
                        </button>

                        <button
                          type="button"
                          className="inventory-alert-action subtract"
                          disabled={updating || quantity <= 0}
                          onClick={() => updateStock(product, "subtract")}
                          title="Subtract stock"
                          aria-label={`Subtract stock from ${product.name || "product"}`}
                        >
                          <BsDashLg />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* PAGINATION */}

      <div className="inventory-alert-pagination">
        <span>
          Showing <strong>{currentProducts.length}</strong> of{" "}
          <strong>{filteredProducts.length}</strong> low-stock products
        </span>

        <div className="inventory-alert-pagination-controls">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => Math.max(page - 1, 1))}
          >
            Previous
          </button>

          <span>
            Page {currentPage} of {totalPages}
          </span>

          <button
            type="button"
            disabled={currentPage >= totalPages}
            onClick={() =>
              setCurrentPage((page) => Math.min(page + 1, totalPages))
            }
          >
            Next
          </button>
        </div>
      </div>
    </>
  );
};

export default InventoryAlertTable;
